import React, { useState, useEffect } from "react";
import { Button, TextInput, Textarea, Modal, Loader } from "@mantine/core";
import apiService from "../../services/api";
import { showAlert } from "../../utils/notify";
import type { TrainerSelectionProps } from "../../types";

interface TrainerFilters {
  search: string;
  philosophy: string;
  expertise: string;
  credentials: string;
  rating: string;
  priceRange: string;
  availability: string;
}

interface TransformedTrainer {
  id: string;
  name: string;
  businessName: string;
  rating: number;
  reviewCount: number;
  first10MinRate: number;
  perMinuteRate: number;
  philosophy: string[];
  expertise: string[];
  credentials: string[];
  bio: string;
  availability: string;
  timeZone: string;
  experienceYears: number;
  image: null;
  raw: Record<string, unknown>;
}

interface LocalSessionOptions {
  sessionDuration: number;
  explanation: string;
}

interface LocalPet {
  id: string;
  pet_name: string;
  race?: string;
  [key: string]: unknown;
}

const TrainerSelection: React.FC<TrainerSelectionProps> = ({
  onBackToClientPortal,
  onSelectTrainer: _onSelectTrainer,
}) => {
  const [trainers, setTrainers] = useState<TransformedTrainer[]>([]);
  const [filteredTrainers, setFilteredTrainers] = useState<TransformedTrainer[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<TrainerFilters>({
    search: "",
    philosophy: "",
    expertise: "",
    credentials: "",
    rating: "",
    priceRange: "",
    availability: "",
  });
  const [selectedTrainer, setSelectedTrainer] = useState<TransformedTrainer | null>(null);
  const [showSessionOptions, setShowSessionOptions] = useState<boolean>(false);
  const [sessionOptions, setSessionOptions] = useState<LocalSessionOptions>({
    sessionDuration: 30,
    explanation: "", // Required description of what they want to achieve
  });
  const [selectedPetId, setSelectedPetId] = useState<string>("");
  const [pets, setPets] = useState<LocalPet[]>([]);
  const [loadingPets, setLoadingPets] = useState<boolean>(false);

  const clientId = localStorage.getItem("clientId");

  useEffect(() => {
    loadTrainers();
    if (clientId) {
      loadPets();
    }
  }, [clientId]);

  const loadPets = async (): Promise<void> => {
    try {
      setLoadingPets(true);
      const response = (await apiService.getClientPets(clientId as string)) as Record<
        string,
        unknown
      >;
      if (response.Data) {
        setPets(response.Data as LocalPet[]);
      }
    } catch (err) {
      console.error("Error loading pets:", err);
    } finally {
      setLoadingPets(false);
    }
  };

  useEffect(() => {
    applyFilters();
  }, [trainers, filters]);

  const toArray = (value: unknown): string[] => {
    if (!value) return [];
    if (Array.isArray(value)) {
      return value
        .map((item) => (typeof item === "string" ? item.trim() : item))
        .filter((item) => Boolean(item)) as string[];
    }
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (!trimmed) return [];

      // Try JSON.parse first (handles JSON arrays with double quotes)
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          return parsed
            .map((item) => (typeof item === "string" ? item.trim() : item))
            .filter((item) => Boolean(item)) as string[];
        }
      } catch (err) {
        console.log("Not valid JSON, trying other parsing methods:", err);
      }

      // Try to parse Python-style list strings (e.g., "['item1', 'item2']")
      // Check if it looks like a Python list (starts with [ and ends with ])
      if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
        try {
          // Replace single quotes with double quotes for JSON parsing
          const jsonString = trimmed.replace(/'/g, '"');
          const parsed = JSON.parse(jsonString);
          if (Array.isArray(parsed)) {
            return parsed
              .map((item) => (typeof item === "string" ? item.trim() : item))
              .filter((item) => Boolean(item)) as string[];
          }
        } catch (err) {
          console.log("Failed to parse as Python-style list, extracting manually:", err);
          // Remove brackets and split by comma, handling quotes
          const inner = trimmed.slice(1, -1).trim();
          if (inner) {
            // Split by comma, but be careful with quotes
            const items = inner
              .split(",")
              .map((item) => {
                let cleaned = item.trim();
                // Remove surrounding quotes if present
                if (
                  (cleaned.startsWith("'") && cleaned.endsWith("'")) ||
                  (cleaned.startsWith('"') && cleaned.endsWith('"'))
                ) {
                  cleaned = cleaned.slice(1, -1);
                }
                return cleaned.trim();
              })
              .filter(Boolean);
            if (items.length > 0) return items;
          }
        }
      }

      // Fall back to comma-separated handling
      return trimmed
        .split(",")
        .map((item) => {
          let cleaned = item.trim();
          // Remove surrounding quotes if present
          if (
            (cleaned.startsWith("'") && cleaned.endsWith("'")) ||
            (cleaned.startsWith('"') && cleaned.endsWith('"'))
          ) {
            cleaned = cleaned.slice(1, -1);
          }
          return cleaned.trim();
        })
        .filter(Boolean);
    }
    return [];
  };

  const loadTrainers = async (): Promise<void> => {
    try {
      setLoading(true);
      const response = (await apiService.getActiveTrainers()) as Record<string, unknown>;

      if (response.Code === 200) {
        const trainerList = ((response.trainers as Record<string, unknown>[]) || []).map(
          (trainer: Record<string, unknown>) => {
            const name =
              `${trainer.first_name || ""} ${trainer.last_name || ""}`.trim() ||
              (trainer.first_name as string) ||
              (trainer.last_name as string) ||
              "Trainer";
            const availabilityLabel = trainer.isOnline ? "Available now" : "Currently offline";
            const availabilityWithZone = trainer.time_zone
              ? `${availabilityLabel} · ${trainer.time_zone}`
              : availabilityLabel;

            // Parse pricing values, handling null/undefined and ensuring they're numbers
            // Handle both string and number types from backend
            const parsePrice = (value: unknown): number => {
              if (value === null || value === undefined || value === "") return 0;
              const parsed = typeof value === "string" ? parseFloat(value) : Number(value);
              return isNaN(parsed) ? 0 : parsed;
            };

            const first10MinRate = parsePrice(trainer.first_10_min_rate);
            const perMinuteRate = parsePrice(trainer.per_minute_rate);

            // Parse specializations - handle Python-style list strings
            const philosophy = toArray(trainer.training_philosophy);
            const expertise = toArray(trainer.areas_of_expertise);
            const credentials = toArray(trainer.credentials);

            return {
              id: trainer.id as string,
              name,
              businessName: (trainer.business_name as string) || "Independent Trainer",
              rating: Number(trainer.average_rating || 0),
              reviewCount: Number(trainer.total_reviews || 0),
              first10MinRate: first10MinRate,
              perMinuteRate: perMinuteRate,
              philosophy: philosophy,
              expertise: expertise,
              credentials: credentials,
              bio: (trainer.bio as string) || "This trainer has not added a bio yet.",
              availability: availabilityWithZone,
              timeZone: (trainer.time_zone as string) || "",
              experienceYears: (trainer.experience_years as number) || 0,
              image: null,
              raw: trainer,
            };
          },
        );

        setTrainers(trainerList);
        setError(null);
      } else {
        setTrainers([]);
        setError((response.Message as string) || "Failed to load trainers");
      }
    } catch (err) {
      console.error("Error loading trainers:", err);
      setError("Failed to load trainers");
      setTrainers([]);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = (): void => {
    let filtered = [...trainers];

    if (filters.search) {
      filtered = filtered.filter(
        (trainer) =>
          trainer.name.toLowerCase().includes(filters.search.toLowerCase()) ||
          trainer.businessName.toLowerCase().includes(filters.search.toLowerCase()) ||
          trainer.bio.toLowerCase().includes(filters.search.toLowerCase()),
      );
    }

    if (filters.philosophy) {
      filtered = filtered.filter((trainer) => trainer.philosophy.includes(filters.philosophy));
    }

    if (filters.expertise) {
      filtered = filtered.filter((trainer) => trainer.expertise.includes(filters.expertise));
    }

    if (filters.credentials) {
      filtered = filtered.filter((trainer) => trainer.credentials.includes(filters.credentials));
    }

    if (filters.rating) {
      const minRating = parseFloat(filters.rating);
      filtered = filtered.filter((trainer) => trainer.rating >= minRating);
    }

    if (filters.priceRange) {
      const [min, max] = filters.priceRange.split("-").map(Number);
      filtered = filtered.filter((trainer) => {
        const totalRate = trainer.first10MinRate + trainer.perMinuteRate * 20; // 30 min session
        return totalRate >= min && totalRate <= max;
      });
    }

    setFilteredTrainers(filtered);
  };

  const handleFilterChange = (filterType: string, value: string): void => {
    setFilters((prev) => ({
      ...prev,
      [filterType]: value,
    }));
  };

  const handleSelectTrainer = (trainer: TransformedTrainer): void => {
    setSelectedTrainer(trainer);
    setShowSessionOptions(true);
  };

  const handleRequestSession = async (): Promise<void> => {
    if (!selectedTrainer) return;
    if (!selectedPetId) {
      showAlert("Please select a pet for this session");
      return;
    }
    if (!sessionOptions.explanation || sessionOptions.explanation.trim().length < 50) {
      showAlert(
        "Please provide a detailed explanation (at least 50 characters) of what you want to achieve in this session",
      );
      return;
    }

    try {
      const response = (await apiService.createSessionRequest({
        client_id: clientId as string,
        trainer_id: selectedTrainer.id,
        pet_id: selectedPetId,
        explanation: sessionOptions.explanation,
        duration_minutes: sessionOptions.sessionDuration,
      })) as Record<string, unknown>;

      if (response.Code === 200) {
        showAlert(
          `Session request submitted! Estimated cost: $${((response.total_price as number) || calculateSessionCost(selectedTrainer, sessionOptions.sessionDuration)).toFixed(2)}. We will notify you once the trainer accepts and it's time to pay.`,
        );
        setShowSessionOptions(false);
        setSelectedTrainer(null);
        setSelectedPetId("");
        setSessionOptions({
          sessionDuration: 30,
          explanation: "",
        });
        if (onBackToClientPortal) {
          onBackToClientPortal();
        }
      } else {
        showAlert(
          "Failed to create session request: " + ((response.Message as string) || "Unknown error"),
        );
      }
    } catch (err) {
      console.error("Error creating session request:", err);
      showAlert("Failed to create session request. Please try again.");
    }
  };

  const calculateSessionCost = (trainer: TransformedTrainer | null, duration: number): number => {
    if (!trainer) {
      return 0;
    }

    // Ensure we're working with numbers
    const first10Min =
      typeof trainer.first10MinRate === "number"
        ? trainer.first10MinRate
        : parseFloat(String(trainer.first10MinRate)) || 0;
    const perMin =
      typeof trainer.perMinuteRate === "number"
        ? trainer.perMinuteRate
        : parseFloat(String(trainer.perMinuteRate)) || 0;

    // Calculate cost: first 10 minutes at first10MinRate, then per minute after
    if (duration <= 10) {
      return first10Min;
    }
    return first10Min + (duration - 10) * perMin;
  };

  const philosophyOptions: string[] = [
    "Force Free / Pure Positive",
    "Positive Reinforcement Based (+R)",
    "Clicker / Marker Training",
    "Fear Free",
    "Science Based",
    "Relationship Based",
    "Balanced",
    "LIMA (Least Intrusive, Minimally Aversive)",
    "Traditional Training Methods",
    "Alpha / Pack Based",
    "Remote Training",
  ];

  const expertiseOptions: string[] = [
    "Puppy",
    "Manners",
    "Behavior Modification",
    "Aggression Management – Dog to dog",
    "Aggression Management - human directed",
    "Reactivity",
    "Separation Anxiety",
    "Anxiety / Fears / Phobias",
    "Leash Walking",
    "Potty Training",
    "Rescue Rehab",
    "Service Dog",
    "Therapy Dog",
    "Protection",
    "Tracking",
    "Competition Obedience",
    "Scentwork",
    "Agility",
    "Conformation",
    "Herding",
    "Earthdog",
  ];

  return (
    <div className="font-sans bg-background min-h-screen text-foreground p-3 sm:p-5">
      {/* Header */}
      <div className="bg-card rounded-2xl p-5 mb-8 border border-border shadow-sm">
        <div className="max-w-[1200px] mx-auto flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-4 cursor-pointer" onClick={onBackToClientPortal}>
            <img
              src="/images/logo.png"
              alt="Pheefee Logo"
              className="w-10 h-10 object-contain rounded-lg"
            />
            <span className="text-2xl font-bold text-foreground">Pheefee</span>
          </div>
          <Button variant="default" onClick={onBackToClientPortal} radius="md">
            ← Back to Client Portal
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-[1200px] mx-auto">
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-2.5 text-primary">
          Choose Your Trainer
        </h1>
        <p className="text-xl text-center mb-10 text-muted-foreground">
          Select from our professional dog trainers based on your specific needs
        </p>

        {/* Filters */}
        <div className="bg-card rounded-2xl p-4 sm:p-8 mb-8 border border-border shadow-lg">
          <h3 className="text-2xl font-semibold mb-5 text-primary">Filter Trainers</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-5">
            <div className="flex flex-col gap-2">
              <label className="text-base font-semibold text-foreground">Search</label>
              <TextInput
                value={filters.search}
                onChange={(e) => handleFilterChange("search", e.target.value)}
                placeholder="Search by name, business, or expertise..."
                classNames={{
                  input: "bg-input border-2 border-border text-foreground rounded-lg text-base",
                }}
              />
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-base font-semibold text-foreground">Training Philosophy</label>
              <select
                value={filters.philosophy}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                  handleFilterChange("philosophy", e.target.value)
                }
                className="p-3 rounded-lg border-2 border-border bg-input text-foreground text-base cursor-pointer"
              >
                <option value="">All Philosophies</option>
                {philosophyOptions.map((philosophy) => (
                  <option key={philosophy} value={philosophy}>
                    {philosophy}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-base font-semibold text-foreground">Area of Expertise</label>
              <select
                value={filters.expertise}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                  handleFilterChange("expertise", e.target.value)
                }
                className="p-3 rounded-lg border-2 border-border bg-input text-foreground text-base cursor-pointer"
              >
                <option value="">All Expertise Areas</option>
                {expertiseOptions.map((expertise) => (
                  <option key={expertise} value={expertise}>
                    {expertise}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-base font-semibold text-foreground">Credentials</label>
              <select
                value={filters.credentials}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                  handleFilterChange("credentials", e.target.value)
                }
                className="p-3 rounded-lg border-2 border-border bg-input text-foreground text-base cursor-pointer"
              >
                <option value="">All Credentials</option>
                <option value="CDT">CDT (Certified Dog Trainer)</option>
                <option value="CPDT">CPDT (Certified Professional Dog Trainer)</option>
                <option value="CPDT-KA">CPDT-KA (CPDT Knowledge Assessed)</option>
                <option value="KPA-CTP">
                  KPA-CTP (Karen Pryor Academy Certified Training Partner)
                </option>
                <option value="CBCC-KA">
                  CBCC-KA (Certified Behavior Consultant Canine - Knowledge Assessed)
                </option>
                <option value="CCPDT">
                  CCPDT (Certification Council for Professional Dog Trainers)
                </option>
                <option value="IAABC">
                  IAABC (International Association of Animal Behavior Consultants)
                </option>
                <option value="VSPDT">VSPDT (Victoria Stilwell Positively Dog Training)</option>
                <option value="ABC">ABC (Animal Behavior College)</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-base font-semibold text-foreground">Minimum Rating</label>
              <select
                value={filters.rating}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                  handleFilterChange("rating", e.target.value)
                }
                className="p-3 rounded-lg border-2 border-border bg-input text-foreground text-base cursor-pointer"
              >
                <option value="">All Ratings</option>
                <option value="4.5">4.5+ Stars</option>
                <option value="4.0">4.0+ Stars</option>
                <option value="3.5">3.5+ Stars</option>
              </select>
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-base font-semibold text-foreground">
                Price Range (30 min session)
              </label>
              <select
                value={filters.priceRange}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                  handleFilterChange("priceRange", e.target.value)
                }
                className="p-3 rounded-lg border-2 border-border bg-input text-foreground text-base cursor-pointer"
              >
                <option value="">All Prices</option>
                <option value="0-30">Under $30</option>
                <option value="30-50">$30 - $50</option>
                <option value="50-80">$50 - $80</option>
                <option value="80-100">$80+</option>
              </select>
            </div>
          </div>
        </div>

        {/* Trainers Grid */}
        {error && (
          <div className="bg-destructive/10 border border-destructive/25 text-destructive p-4 rounded-xl mb-5 text-center">
            {error}
          </div>
        )}
        {loading && (
          <div className="text-center text-primary text-lg mt-5">
            <Loader color="orange" size="md" className="mx-auto mb-2" />
            Loading trainers...
          </div>
        )}

        {!loading && !error && trainers.length === 0 && (
          <div className="text-center text-muted-foreground text-xl mt-10">
            No trainers are currently accepting new sessions. Please check back soon.
          </div>
        )}

        {!loading && !error && trainers.length > 0 && filteredTrainers.length === 0 && (
          <div className="text-center text-muted-foreground text-xl mt-10">
            No trainers found matching your criteria. Try adjusting your filters.
          </div>
        )}

        {!loading && !error && filteredTrainers.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-[repeat(auto-fit,minmax(300px,1fr))] gap-5">
            {filteredTrainers.map((trainer) => (
              <div
                key={trainer.id}
                className="bg-card rounded-2xl p-6 border border-border transition-all duration-300 cursor-pointer relative text-foreground hover:-translate-y-1 hover:shadow-lg hover:border-primary/30"
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-foreground m-0">{trainer.name}</h3>
                  <div className="flex items-center gap-1 text-sm text-foreground">
                    ⭐ {trainer.rating} ({trainer.reviewCount} reviews)
                  </div>
                </div>

                <div className="text-sm text-muted-foreground mb-2.5">{trainer.businessName}</div>
                <div className="text-sm text-muted-foreground leading-relaxed mb-4">
                  {trainer.bio}
                </div>

                {/* Training Philosophy */}
                {trainer.philosophy && trainer.philosophy.length > 0 && (
                  <div className="mb-3">
                    <div className="text-[0.85rem] text-foreground mb-1.5 font-semibold">
                      Training Philosophy:
                    </div>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {trainer.philosophy.map((phil) => (
                        <span
                          key={phil}
                          className="bg-primary-subtle text-primary px-3 py-1.5 rounded-full text-xs font-medium border border-primary/20 inline-block"
                        >
                          {phil}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Areas of Expertise / Specializations */}
                {trainer.expertise && trainer.expertise.length > 0 && (
                  <div className="mb-3">
                    <div className="text-[0.85rem] text-foreground mb-1.5 font-semibold">
                      Specializations:
                    </div>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {trainer.expertise.map((exp) => (
                        <span
                          key={exp}
                          className="bg-primary-subtle text-primary px-3 py-1.5 rounded-full text-xs font-medium border border-primary/20 inline-block"
                        >
                          {exp}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Credentials */}
                {trainer.credentials && trainer.credentials.length > 0 && (
                  <div className="mb-3">
                    <div className="text-[0.85rem] text-foreground mb-1.5 font-semibold">
                      Credentials:
                    </div>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {trainer.credentials.map((cred) => (
                        <span
                          key={cred}
                          className="bg-primary-subtle text-primary px-3 py-1.5 rounded-full text-xs font-medium border border-primary/20 inline-block"
                        >
                          {cred}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-between mb-4">
                  <div className="text-sm text-muted-foreground">
                    <div>30-min session:</div>
                    <div className="text-xl font-bold text-foreground">
                      ${calculateSessionCost(trainer, 30).toFixed(2)}
                    </div>
                  </div>
                  <div className="text-sm text-muted-foreground font-semibold">
                    {trainer.availability}
                  </div>
                </div>

                <Button fullWidth radius="md" onClick={() => handleSelectTrainer(trainer)}>
                  Select This Trainer
                </Button>
              </div>
            ))}
          </div>
        )}

        {/* Session Options Modal */}
        <Modal
          opened={showSessionOptions && selectedTrainer !== null}
          onClose={() => setShowSessionOptions(false)}
          title="Session Options"
          centered
          radius="lg"
          size="lg"
        >
          {selectedTrainer && (
            <div>
              <div className="text-xl font-semibold mb-8 text-primary text-center">
                Selected: {selectedTrainer.name}
              </div>

              <div className="flex flex-col gap-2 mb-5">
                <label className="text-base font-semibold text-foreground">Select Pet *</label>
                {loadingPets ? (
                  <Loader size="sm" />
                ) : pets.length === 0 ? (
                  <div className="text-destructive text-sm">
                    No pets found. Please add a pet in your profile first.
                  </div>
                ) : (
                  <select
                    value={selectedPetId}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                      setSelectedPetId(e.target.value)
                    }
                    className="p-3 rounded-lg border-2 border-border bg-card text-foreground text-base cursor-pointer"
                    required
                  >
                    <option value="">Select a pet...</option>
                    {pets.map((pet) => (
                      <option key={pet.id} value={pet.id}>
                        {pet.pet_name} {pet.race ? `(${pet.race})` : ""}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              <div className="flex flex-col gap-2 mb-5">
                <label className="text-base font-semibold text-foreground">
                  Session Duration (minutes) *
                </label>
                <select
                  value={sessionOptions.sessionDuration}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    setSessionOptions({
                      ...sessionOptions,
                      sessionDuration: parseInt(e.target.value),
                    })
                  }
                  className="p-3 rounded-lg border-2 border-border bg-card text-foreground text-base cursor-pointer"
                >
                  <option value={10}>10 minutes</option>
                  <option value={15}>15 minutes</option>
                  <option value={30}>30 minutes</option>
                  <option value={45}>45 minutes</option>
                  <option value={60}>60 minutes</option>
                  <option value={90}>90 minutes</option>
                </select>
              </div>

              <div className="flex flex-col gap-2 mb-[30px]">
                <label className="text-base font-semibold text-foreground">
                  What are you looking to achieve in this session? *
                  <span className="text-[0.85rem] text-muted-foreground font-normal">
                    {" "}
                    (Minimum 50 characters)
                  </span>
                </label>
                <Textarea
                  value={sessionOptions.explanation}
                  onChange={(e) =>
                    setSessionOptions({
                      ...sessionOptions,
                      explanation: e.target.value,
                    })
                  }
                  placeholder="Please provide a detailed explanation of what you want to achieve in this training session. This helps the trainer understand your goals and prepare accordingly..."
                  minLength={50}
                  required
                  minRows={3}
                  autosize
                  classNames={{
                    input: "border-2 border-border bg-card text-foreground text-base rounded-lg",
                  }}
                />
                <div
                  className={`text-[0.85rem] mt-1 ${sessionOptions.explanation.length < 50 ? "text-destructive" : "text-success"}`}
                >
                  {sessionOptions.explanation.length}/50 characters
                </div>
              </div>

              <div className="bg-success/10 border border-success/30 rounded-lg p-4 mb-8 text-center">
                <div className="text-base text-foreground mb-1">Session Cost:</div>
                <div className="text-2xl font-bold text-success">
                  $
                  {calculateSessionCost(selectedTrainer, sessionOptions.sessionDuration).toFixed(2)}
                </div>
              </div>

              <div className="flex gap-4 justify-center">
                <Button
                  radius="md"
                  onClick={handleRequestSession}
                  disabled={
                    !selectedPetId ||
                    !sessionOptions.explanation ||
                    sessionOptions.explanation.trim().length < 50
                  }
                  size="lg"
                >
                  Proceed to Payment
                </Button>
                <Button
                  variant="outline"
                  color="red"
                  size="lg"
                  className="font-semibold text-base transition-all duration-300"
                  onClick={() => setShowSessionOptions(false)}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </Modal>
      </div>
    </div>
  );
};

export default TrainerSelection;
